import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import ProductSectionCarousel from './ProductSectionCarousel';
import { Product } from '../types';
import { isSliderLayout, getProductsPerRow } from '../utils/homepageSectionLayout';

export interface DynamicHomepageSection {
  _id: string;
  title: string;
  subtitle?: string;
  slug?: string;
  type: string;
  layoutMode?: 'grid' | 'slider';
  products: Product[];
  config?: {
    productsPerRow?: number;
    productLimit?: number;
    layout?: string;
  };
  order: number;
}

const PRODUCT_SECTION_TYPES = new Set([
  'product_slider',
  'product-slider',
  'trending_products',
  'recently_viewed',
  'custom_products',
  'product_grid',
  'product-grid',
  'featured_products',
  'top_selling',
  'new_arrivals',
  'recommended_products',
  'popular_products',
  'category_products',
]);

export function isProductHomepageSection(section: DynamicHomepageSection): boolean {
  return PRODUCT_SECTION_TYPES.has(section.type);
}

function getProducts(section: DynamicHomepageSection): Product[] {
  const list = section.products || [];
  const limit = section.config?.productLimit || 24;
  return list.slice(0, limit);
}

interface Props {
  sections: DynamicHomepageSection[];
  carouselIndexOffset?: number;
}

const HomepageProductSections: React.FC<Props> = ({
  sections,
  carouselIndexOffset = 0,
}) => {
  const carouselRefs = useRef<(HTMLDivElement | null)[]>([]);
  let carouselSlot = carouselIndexOffset;

  return (
    <>
      {sections.map((section) => {
        if (!isProductHomepageSection(section)) return null;
        const products = getProducts(section);
        if (!products.length) return null;

        if (isSliderLayout(section)) {
          const idx = carouselSlot++;
          return (
            <ProductSectionCarousel
              key={section._id}
              title={section.title}
              subtitle={section.subtitle}
              products={products}
              trackRef={(el) => {
                carouselRefs.current[idx] = el;
              }}
            />
          );
        }

        const perRow = getProductsPerRow(section);
        const gridClass =
          perRow === 2
            ? 'grid-cols-2'
            : perRow === 3
              ? 'grid-cols-2 md:grid-cols-3'
              : perRow === 5
                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
                : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

        return (
          <section key={section._id} className="py-8 md:py-12 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="text-center mb-6 md:mb-8">
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
                  {section.title}
                </h2>
                {section.subtitle && (
                  <p className="text-gray-600 text-sm md:text-base">{section.subtitle}</p>
                )}
              </div>
              <div className={`grid ${gridClass} gap-4 md:gap-6`}>
                {products.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
              <div className="text-center mt-6">
                <Link
                  to="/products"
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm md:text-base"
                >
                  View all products →
                </Link>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
};

export default HomepageProductSections;
